// src/pages/DdosTestingPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PageTransition from '../components/Shared/PageTransition';
import ServerInfoCard from '../components/Shared/ServerInfoCard';
import DdosHero from '../components/DdosTesting/DdosHero';
import DdosAttackTypeSelector from '../components/DdosTesting/DdosAttackTypeSelector';
import DdosConfigCard, { ATTACK_METHODS } from '../components/DdosTesting/DdosConfigCard';
import DdosTerminal from '../components/DdosTesting/DdosTerminal';
import DdosCharts from '../components/DdosTesting/DdosCharts';
import DdosProTips from '../components/DdosTesting/DdosProTips';
// Trigger hot reload for tips grid
import TestResults from '../components/DdosTesting/TestResults';
import ProtectionAnalysis from '../components/DdosTesting/ProtectionAnalysis';

import TermsOfServiceModal from '../components/Shared/TermsOfServiceModal';
import CaptchaModal from '../components/BotConnection/CaptchaModal';

import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import { WS_DDOS_TYPES, WS_DDOS_TOPICS, getAppEndpoint, getUserQueueTopic } from '../config/wsDdosConfig';
import { useServerConnection } from '../contexts/ServerConnectionContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { handleWebSocketError } from '../utils/websocketErrorHandler';
import { useBackgroundMusic } from '../hooks/useBackgroundMusic';
import MusicControlPanel from '../components/Shared/MusicControlPanel';
import GameContainer from '../components/Shared/GameContainer';

import './DdosPage.css';

const formatTimeStr = (date = new Date()) => {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `[${h}:${m}:${s}]`;
};

const DdosTestingPage = ({ openModal }) => {
  const { language, t } = useLanguage();
  const isEn = language === 'en';

  const { isAuthenticated } = useAuth();
  const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
  const { serverInfo, isConnectedToServer, connectionError, isConnecting, targetIp } = useServerConnection();
  const { addNotification, showToast } = useNotifications();

  // Target info derived from connection context
  const currentHost = serverInfo?.hostname || serverInfo?.ip || targetIp || '';
  const currentPort = serverInfo?.port || null;
  const fullTargetAddress = currentHost
    ? currentPort && !currentHost.includes(':')
      ? `${currentHost}:${currentPort}`
      : currentHost
    : 'mc.hypixel.net:25565';

  // --- Configuration State ---
  const [attackType, setAttackType] = useState('packet'); // 'packet' | 'bot'
  const [selectedMethod, setSelectedMethod] = useState('tcp_syn_flood');
  const [threads, setThreads] = useState(100);
  const [duration, setDuration] = useState(60); // seconds, 0 = unlimited
  const [proxyEnabled, setProxyEnabled] = useState(true);
  const [randomizeEnabled, setRandomizeEnabled] = useState(true);
  const [failSafeEnabled, setFailSafeEnabled] = useState(true);

  // --- Execution & Progress State ---
  const [attackState, setAttackState] = useState({
    status: 'idle', // 'idle' | 'starting' | 'running' | 'stopping' | 'completed' | 'error'
    message: null,
    isLoading: false
  });
  const isAttackActive = attackState.status === 'running' || attackState.status === 'starting';
  const music = useBackgroundMusic(isAttackActive);

  const [packetsSent, setPacketsSent] = useState(0);
  const [dataMb, setDataMb] = useState(0);
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const [activeWorkers, setActiveWorkers] = useState(0);
  const [attackFinalResults, setAttackFinalResults] = useState(null);
  const [protectionInfo, setProtectionInfo] = useState(null);
  const [ddosError, setDdosError] = useState(null);

  // --- Modals State ---
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [captchaData, setCaptchaData] = useState(null);
  const [isCaptchaModalOpen, setIsCaptchaModalOpen] = useState(false);

  // --- Real-time Chart Data (31 points for -30s to Now) ---
  const [chartData, setChartData] = useState({
    pps: Array(31).fill(0),
    rtt: Array(31).fill(0)
  });

  // Current instantaneous values
  const currentPpsRef = useRef(0);
  const currentRttRef = useRef(0);

  // --- Terminal Logs State ---
  const [logs, setLogs] = useState(() => [
    {
      time: '[00:00:00]',
      tagClass: 'system',
      tagText: '[SYSTEM]',
      text: 'Демон тестирования устойчивости StormBlack v4.9 готов к работе.'
    },
    {
      time: '[00:00:00]',
      tagClass: 'target',
      tagText: '[TARGET]',
      text: 'Целевой узел mc.hypixel.net:25565 в сети.'
    },
    {
      time: '[00:00:00]',
      tagClass: 'metric',
      tagText: '[CONFIG]',
      text: 'Выберите вектор атаки, настройте количество потоков и нажмите «Запустить стресс-тест».'
    }
  ]);

  const addLog = useCallback((tagClass, tagText, text) => {
    setLogs((prev) => {
      const updated = [...prev, { time: formatTimeStr(), tagClass, tagText, text }];
      return updated.length > 400 ? updated.slice(updated.length - 400) : updated;
    });
  }, []);

  // Sync log when target updates
  useEffect(() => {
    if (fullTargetAddress) {
      addLog('target', '[TARGET]', `Целевой сервер обновлен: ${fullTargetAddress}`);
    }
  }, [fullTargetAddress, addLog]);

  // Attack type switcher
  const handleSelectAttackType = useCallback((type) => {
    if (attackState.status === 'running' || attackState.isLoading) {
      showToast(isEn ? 'Stop the current attack before switching type' : 'Остановите текущий стресс-тест перед сменой типа');
      return;
    }
    setAttackType(type);
    const defaultMethod = type === 'bot' ? 'null_handshake' : 'tcp_syn_flood';
    setSelectedMethod(defaultMethod);
    addLog('system', '[MODE]', `Переключен тип атаки: ${type === 'bot' ? 'DDoS-атака с помощью ботов (L7)' : 'Пакетная DDoS-атака (L4)'}.`);
    showToast(isEn ? `Selected mode: ${type === 'bot' ? 'Bots L7' : 'Packet L4'}` : `Выбран режим: ${type === 'bot' ? 'Боты L7' : 'Пакетный L4'}`);
  }, [attackState, isEn, addLog, showToast]);

  const handleSelectMethod = useCallback((methodId) => {
    setSelectedMethod(methodId);
    const list = ATTACK_METHODS[attackType] || [];
    const found = list.find((m) => m.id === methodId);
    const title = found ? found.title : methodId;
    addLog('metric', '[METHOD]', `Выбран метод: ${title}`);
    showToast(isEn ? `Method selected: ${title}` : `Метод: ${title}`);
  }, [attackType, isEn, addLog, showToast]);

  const handleThreadsChange = useCallback((newThreads) => {
    setThreads(newThreads);
    addLog('system', '[THREADS]', `Количество рабочих потоков установлено: ${newThreads}`);
  }, [addLog]);

  const handleDurationChange = useCallback((newDuration) => {
    setDuration(newDuration);
    const durationLabel = newDuration === 0 ? (isEn ? 'Unlimited' : 'Бессрочно') : `${newDuration} ${isEn ? 'sec' : 'сек'}`;
    addLog('system', '[DURATION]', `Длительность теста установлена: ${durationLabel}`);
    showToast(isEn ? `Duration: ${durationLabel}` : `Длительность: ${durationLabel}`);
  }, [isEn, addLog, showToast]);

  // Terminal actions
  const handleClearLogs = useCallback(() => {
    setLogs([
      {
        time: formatTimeStr(),
        tagClass: 'system',
        tagText: '[SYSTEM]',
        text: isEn ? 'Terminal cleared.' : 'Терминал очищен.'
      }
    ]);
    showToast(isEn ? 'Terminal cleared' : 'Терминал очищен');
  }, [isEn, showToast]);

  const handleCopyLogs = useCallback(() => {
    const text = logs.map((l) => `${l.time} ${l.tagText} ${l.text}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      showToast(isEn ? 'Logs copied to clipboard' : 'Логи скопированы в буфер');
    });
  }, [logs, isEn, showToast]);

  // Captcha interaction
  const handleInteractCaptchaTile = useCallback((entityId, index) => {
    if (sendMessage) {
      sendMessage(getAppEndpoint(WS_DDOS_TOPICS.INTERACT_CAPTCHA), {
        type: WS_DDOS_TYPES.INTERACT_CAPTCHA,
        entityId,
        index,
        botName: captchaData?.botName
      });
      addLog('system', '[CAPTCHA]', `Повернут тайл #${index + 1} для ${captchaData?.botName || 'бота'}`);
    }
  }, [sendMessage, captchaData, addLog]);

  // Session timer ticker
  const sessionTimerRef = useRef(null);

  const startSessionTimer = useCallback(() => {
    clearInterval(sessionTimerRef.current);

    sessionTimerRef.current = setInterval(() => {
      setSessionSeconds((s) => {
        const nextSec = s + 1;
        // Check duration limit
        if (duration > 0 && nextSec >= duration) {
          addLog('alert', '[TIMER]', `Заданное время теста (${duration} сек) истекло. Автоматическая остановка.`);
          executeStopAttack();
        }
        return nextSec;
      });

      // Shift chart history every second using real metric values
      setChartData((prev) => {
        const nextPps = [...prev.pps.slice(1), currentPpsRef.current];
        const nextRtt = [...prev.rtt.slice(1), currentRttRef.current];
        return { pps: nextPps, rtt: nextRtt };
      });

      // Fail-Safe check based on real server RTT
      if (failSafeEnabled && currentRttRef.current >= 1000) {
        addLog('alert', '[FAILSAFE]', 'Внимание! RTT превысил 1000 ms. Аварийный предохранитель остановил тест.');
        executeStopAttack();
      }
    }, 1000);
  }, [duration, failSafeEnabled, addLog]);

  const stopSessionTimer = useCallback(() => {
    clearInterval(sessionTimerRef.current);
    currentPpsRef.current = 0;
    setChartData((prev) => ({
      pps: [...prev.pps.slice(1), 0],
      rtt: [...prev.rtt.slice(1), currentRttRef.current]
    }));
  }, []);

  // Format mm:ss
  const formatSessionTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  // --- WebSocket Subscriptions ---
  const subscriptionsRef = useRef({ status: null, progress: null, error: null });

  const lastLoggedPacketsRef = useRef(0);

  const handleDdosStatusUpdate = useCallback((message) => {
    setDdosError(null);
    switch (message?.type) {
      case WS_DDOS_TYPES.ATTACK_STARTED: {
        const msg = message.data?.message || `Attack (${message.data?.attackType || attackType}) started.`;
        setAttackState({ status: 'running', message: msg, isLoading: false });
        setActiveWorkers(threads);
        setPacketsSent(0);
        setDataMb(0);
        setSessionSeconds(0);
        setAttackFinalResults(null);
        lastLoggedPacketsRef.current = 0;

        // Initialize instantaneous metrics with clean baseline
        currentPpsRef.current = 0;
        currentRttRef.current = serverInfo?.ping || 0;

        startSessionTimer();
        addLog('system', '[START]', `Инициализация нагрузочного пула: ${threads} воркеров запущены.`);
        addLog('target', '[TARGET]', `Цель: ${fullTargetAddress} • Метод: ${selectedMethod}`);
        showToast(isEn ? 'Stress test started' : 'Стресс-тест успешно запущен');
        break;
      }
      case WS_DDOS_TYPES.ATTACK_STOPPED: {
        const msg = message.data?.message || 'Attack stopped.';
        setAttackState({ status: 'idle', message: msg, isLoading: false });
        setActiveWorkers(0);
        stopSessionTimer();
        window.dispatchEvent(new CustomEvent('showAiInvite', { detail: { message: t('aiAssistantInviteDdos') } }));
        addLog('alert', '[STOP]', `Стресс-тестирование остановлено: ${msg}`);
        showToast(isEn ? 'Attack stopped' : 'Тестирование остановлено');
        break;
      }
      case WS_DDOS_TYPES.ATTACK_COMPLETED: {
        const msg = message.data?.message || 'Attack finished.';
        setAttackState({ status: 'completed', message: msg, isLoading: false });
        setActiveWorkers(0);
        stopSessionTimer();
        setAttackFinalResults(message.data?.results || {});
        setProtectionInfo(message.data?.protectionAnalysis || null);
        window.dispatchEvent(new CustomEvent('showAiInvite', { detail: { message: t('aiAssistantInviteDdos') } }));
        addLog('success', '[FINISH]', `Стресс-тест успешно завершен. Итого: ${packetsSent.toLocaleString()} пакетов (${dataMb.toFixed(1)} MB).`);
        showToast(isEn ? 'Attack completed' : 'Тестирование завершено');
        break;
      }
      case WS_DDOS_TYPES.CURRENT_STATE: {
        if (message.attack?.status === 'running') {
          setAttackState({ status: 'running', message: 'Reconnected to ongoing attack.', isLoading: false });
          setActiveWorkers(threads);
          startSessionTimer();
        }
        break;
      }
      case WS_DDOS_TYPES.CAPTCHA_REQUIRED:
      case 'BOT_CAPTCHA_REQUIRED': {
        const payload = message?.data || message;
        setCaptchaData(payload);
        setIsCaptchaModalOpen(true);
        addLog('alert', '[CAPTCHA]', `Требуется решение 3x3 пазл-капчи для ${payload?.botName || 'клиента'}`);
        break;
      }
      default:
        break;
    }
  }, [attackType, threads, fullTargetAddress, selectedMethod, isEn, packetsSent, dataMb, serverInfo, startSessionTimer, stopSessionTimer, addLog, showToast, t]);

  const handleDdosProgressUpdate = useCallback((message) => {
    if (message?.type === WS_DDOS_TYPES.ATTACK_PROGRESS && message.data) {
      const d = message.data;
      if (d.packetsSent !== undefined) setPacketsSent(d.packetsSent);
      if (d.dataSent !== undefined) {
        const num = parseFloat(d.dataSent);
        if (!isNaN(num)) setDataMb(num);
      }
      if (d.trafficRate?.packetsPerSec !== undefined) {
        currentPpsRef.current = d.trafficRate.packetsPerSec;
      }
      if (d.serverPing !== undefined && d.serverPing > 0) {
        currentRttRef.current = d.serverPing;
      }

      // Log real flooder events / status updates
      if (d.logMessage) {
        let tag = '[GO-ENGINE]';
        let text = d.logMessage;
        let tagClass = 'attack';
        if (text.startsWith('[STATUS]')) {
          tag = '[STATUS]';
          text = text.replace(/^\[STATUS\]\s*/, '');
          tagClass = text.includes('Err') ? 'alert' : 'metric';
        }
        addLog(tagClass, tag, text);
      } else if (d.packetsSent && d.packetsSent > 0 && d.packetsSent !== lastLoggedPacketsRef.current) {
        const pps = d.trafficRate?.packetsPerSec || 0;
        const ppsText = pps > 0 ? ` [${pps.toLocaleString()} p/s]` : '';
        addLog('attack', '[ATTACK]', `Отправлено: ${d.packetsSent.toLocaleString()} пакетов${ppsText} • Объем: ${d.dataSent || '0 B'}`);
        lastLoggedPacketsRef.current = d.packetsSent;
      }
    }
  }, [addLog]);

  const handleDdosErrorUpdate = useCallback((errorDataFromServer) => {
    const errorMsg = errorDataFromServer?.error || errorDataFromServer?.message || 'An unspecified DDoS error occurred.';
    setDdosError(errorMsg);
    setAttackState({ status: 'error', message: errorMsg, isLoading: false });
    setActiveWorkers(0);
    stopSessionTimer();
    addLog('error', '[ERROR]', errorMsg);
    handleWebSocketError(errorDataFromServer, null, openModal, addNotification, {
      defaultTitle: 'DDoS Operation Error',
      moduleName: 'DDoS Testing'
    });
    showToast(isEn ? `Error: ${errorMsg}` : `Ошибка: ${errorMsg}`);
  }, [openModal, addNotification, stopSessionTimer, addLog, showToast, isEn]);

  // Subscribe to WebSocket channels
  useEffect(() => {
    if (!isWebSocketConnected || !isAuthenticated) {
      Object.values(subscriptionsRef.current).forEach((unsub) => unsub?.());
      subscriptionsRef.current = { status: null, progress: null, error: null };
      return;
    }

    Object.values(subscriptionsRef.current).forEach((unsub) => unsub?.());
    subscriptionsRef.current.status = subscribe(getUserQueueTopic(WS_DDOS_TOPICS.STATUS), handleDdosStatusUpdate);
    subscriptionsRef.current.progress = subscribe(getUserQueueTopic(WS_DDOS_TOPICS.PROGRESS), handleDdosProgressUpdate);
    subscriptionsRef.current.error = subscribe(getUserQueueTopic(WS_DDOS_TOPICS.ERROR), handleDdosErrorUpdate);

    if (serverInfo && attackState.status === 'idle') {
      sendMessage(getAppEndpoint(WS_DDOS_TOPICS.GET_STATE), {});
    }

    return () => {
      Object.values(subscriptionsRef.current).forEach((unsub) => unsub?.());
      subscriptionsRef.current = { status: null, progress: null, error: null };
    };
  }, [isWebSocketConnected, isAuthenticated, subscribe, sendMessage, serverInfo, handleDdosStatusUpdate, handleDdosProgressUpdate, handleDdosErrorUpdate]);

  // Execute Start Attack
  const executeStartAttack = useCallback(() => {
    const targetAddress = fullTargetAddress || serverInfo?.ip || targetIp;
    if (!targetAddress) {
      showToast(isEn ? 'Please connect to a target server first' : 'Сначала подключитесь к целевому серверу');
      return;
    }

    setAttackState({ status: 'starting', message: 'Initiating attack...', isLoading: true });
    setDdosError(null);
    setAttackFinalResults(null);

    const attackConfigPayload = {
      type: attackType,
      method: selectedMethod,
      intensity: 100,
      threads: threads,
      duration: {
        value: duration,
        unit: 'seconds'
      },
      proxy: proxyEnabled,
      randomize: randomizeEnabled,
      failSafe: failSafeEnabled
    };

    if (sendMessage) {
      sendMessage(getAppEndpoint(WS_DDOS_TOPICS.START), {
        ip: targetAddress,
        attackConfig: attackConfigPayload
      });
      addLog('system', '[DISPATCH]', `Команда запуска отправлена на ядро: /app/ddos/start (${targetAddress})`);
    } else {
      // Fallback local start simulation if WS is offline
      setTimeout(() => {
        handleDdosStatusUpdate({
          type: WS_DDOS_TYPES.ATTACK_STARTED,
          data: { attackType, message: 'Attack started (Local daemon mode)' }
        });
      }, 500);
    }
  }, [fullTargetAddress, serverInfo, targetIp, attackType, selectedMethod, threads, duration, proxyEnabled, randomizeEnabled, failSafeEnabled, sendMessage, isEn, addLog, showToast, handleDdosStatusUpdate]);

  // Execute Stop Attack
  const executeStopAttack = useCallback(() => {
    const targetAddress = fullTargetAddress || serverInfo?.ip || targetIp;
    setAttackState({ status: 'stopping', message: 'Stopping attack...', isLoading: true });
    setDdosError(null);

    if (sendMessage) {
      sendMessage(getAppEndpoint(WS_DDOS_TOPICS.STOP), { ip: targetAddress });
      addLog('system', '[DISPATCH]', `Команда остановки отправлена на ядро: /app/ddos/stop (${targetAddress})`);
    } else {
      setTimeout(() => {
        handleDdosStatusUpdate({
          type: WS_DDOS_TYPES.ATTACK_STOPPED,
          data: { message: 'Attack stopped by user' }
        });
      }, 400);
    }
  }, [fullTargetAddress, serverInfo, targetIp, sendMessage, addLog, handleDdosStatusUpdate]);

  // Handle Start clicked (prompts legal Terms first, opens login if guest)
  const handleStartClicked = useCallback(() => {
    if (!isAuthenticated) {
      if (openModal) {
        openModal('login');
      } else {
        showToast(isEn ? 'Please log in to start stress testing' : 'Войдите в аккаунт для запуска стресс-теста');
      }
      return;
    }
    setPendingAction('start');
    setIsTermsModalOpen(true);
  }, [isAuthenticated, openModal, isEn, showToast]);

  const isRunning = attackState.status === 'running';
  const isAttackLoading = attackState.isLoading || attackState.status === 'starting' || attackState.status === 'stopping';
  const isDisabled = isConnecting || isAttackLoading;

  return (
    <PageTransition>
      <div className="ddos-page-container min-h-screen text-white relative font-sans">
        <div className="relative z-10 max-w-[1320px] mx-auto px-3 sm:px-6 pt-6 sm:pt-14">
          {/* 1. Hero: Bebas Neue DDOS */}
          <DdosHero />

          {/* 2. Target Server Card (Shared ServerInfoCard matching Apple / Grok standard) */}
          <ServerInfoCard className="w-full mx-auto relative z-40 mb-4 sm:mb-[30px]" />

          {/* 3. Attack Type Selection (Bots L7 vs Packet L4) */}
          <DdosAttackTypeSelector
            selectedType={attackType}
            onSelectType={handleSelectAttackType}
            disabled={isRunning || isAttackLoading}
          />

          {/* 4. Main Grid: Config Card (Left) + Terminal (Right) */}
          <div className="ddos-main-grid">
            <DdosConfigCard
              attackType={attackType}
              selectedMethod={selectedMethod}
              onSelectMethod={handleSelectMethod}
              threads={threads}
              onThreadsChange={handleThreadsChange}
              duration={duration}
              onDurationChange={handleDurationChange}
              proxyEnabled={proxyEnabled}
              onProxyToggle={setProxyEnabled}
              randomizeEnabled={randomizeEnabled}
              onRandomizeToggle={setRandomizeEnabled}
              failSafeEnabled={failSafeEnabled}
              onFailSafeToggle={setFailSafeEnabled}
              isRunning={isRunning}
              isLoading={isAttackLoading}
              isDisabled={isDisabled}
              onStart={handleStartClicked}
              onStop={executeStopAttack}
            />

            <div className="flex flex-col gap-4 min-w-0">
              <MusicControlPanel
                isPlaying={isAttackActive}
                volume={music.volume}
                setVolume={music.setVolume}
                isMuted={music.isMuted}
                setIsMuted={music.setIsMuted}
                currentTime={music.currentTime}
                currentTrack={music.currentTrack}
                lyrics={music.lyrics}
                analyser={music.analyser}
                accentColor="red"
                onNextTrack={music.nextTrack}
              />
              <GameContainer
                isActive={isAttackActive}
                accentColor="red"
              />
              <DdosTerminal
                logs={logs}
                packetsSent={packetsSent}
                dataMb={dataMb}
                sessionTime={formatSessionTime(sessionSeconds)}
                isRunning={isRunning}
                activeWorkers={activeWorkers}
                onClearLogs={handleClearLogs}
                onCopyLogs={handleCopyLogs}
              />
            </div>
          </div>

          {/* 5. Dynamic Real-time Canvas Charts */}
          <DdosCharts ppsHistory={chartData.pps} rttHistory={chartData.rtt} />

          {/* 6. Test Results & Protection Analysis (Rendered when available) */}
          {attackFinalResults && (
            <div className="mb-10">
              <TestResults results={attackFinalResults} />
            </div>
          )}

          {protectionInfo && (
            <div className="mb-10">
              <ProtectionAnalysis
                protections={protectionInfo.detected || []}
                recommendations={protectionInfo.recommendations || []}
              />
            </div>
          )}

          {/* 7. Pro Tips & Resilience Guidelines */}
          <DdosProTips />
        </div>

        {/* Legal Terms of Service Modal */}
        <TermsOfServiceModal
          isOpen={isTermsModalOpen}
          onClose={() => setIsTermsModalOpen(false)}
          onAccept={() => {
            setIsTermsModalOpen(false);
            if (pendingAction === 'start') executeStartAttack();
            setPendingAction(null);
          }}
          onDecline={() => {
            setIsTermsModalOpen(false);
            setPendingAction(null);
          }}
          t={t}
        />

        {/* Captcha Modal for Frame Puzzles */}
        <CaptchaModal
          isOpen={isCaptchaModalOpen}
          captchaData={captchaData}
          onInteractTile={handleInteractCaptchaTile}
          onClose={() => setIsCaptchaModalOpen(false)}
          onSkip={() => setIsCaptchaModalOpen(false)}
        />
      </div>
    </PageTransition>
  );
};

export default DdosTestingPage;